import { c as _c } from "react/compiler-runtime";
import { Stack, TextField, Typography, Autocomplete } from '@mui/material';
import type { ResourceIdentifiers } from '@repo/api-gw-sdk';
import { forwardRef, type HTMLAttributes, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { ResourceIdentifierItem } from './resourceIdentifierItem';
import { AddButton } from '../buttons/addButton';
const ITEM_HEIGHT = 56;
const MAX_LISTBOX_HEIGHT = 300;

// Virtualized listbox for the Autocomplete dropdown.
const VirtualizedListbox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(function VirtualizedListbox(t0, ref) {
  const $ = _c(17);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children) {
    t1 = Array.isArray(children) ? children.flat() : [];
    $[3] = children;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const items = t1;
  const height = Math.min(items.length * ITEM_HEIGHT, MAX_LISTBOX_HEIGHT);
  let t2;
  if ($[5] !== height) {
    t2 = {
      height
    };
    $[5] = height;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== items) {
    t3 = index => items[index];
    $[7] = items;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== items.length || $[10] !== t2 || $[11] !== t3) {
    t4 = <Virtuoso style={t2} totalCount={items.length} itemContent={t3} />;
    $[9] = items.length;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== props || $[14] !== ref || $[15] !== t4) {
    t5 = <div ref={ref} {...props}>{t4}</div>;
    $[13] = props;
    $[14] = ref;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
});
interface Props {
  label: string;
  description: string;
  allResources: ResourceIdentifiers[];
  filteredResources?: ResourceIdentifiers[];
  resources: string[];
  initialResourcesIds?: string[];
  addResource: (providerResourceId: string) => void;
  removeResource: (providerResourceId: string) => void;
  disabled?: boolean;
  disabledDeletion?: boolean;
  freeTextAdd?: boolean;
  onFilterChange?: (text: string) => void;
}
export const ResourcesSelectorAccordion = props => {
  const $ = _c(21);
  const [isExpanded, setIsExpanded] = useState(false);
  let t0;
  if ($[0] !== isExpanded) {
    t0 = () => setIsExpanded(!isExpanded);
    $[0] = isExpanded;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "100%",
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.label) {
    t2 = <Typography variant="body1">{props.label}</Typography>;
    $[3] = props.label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isExpanded || $[6] !== props.resources) {
    t3 = !isExpanded && <Typography sx={{
      fontWeight: "400 !important"
    }} variant="subtitle1">{props.resources.length}</Typography>;
    $[5] = isExpanded;
    $[6] = props.resources;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <Stack direction="row" sx={t1}>{t2}{t3}</Stack>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t0 || $[12] !== t4) {
    t5 = <AccordionSummary onClick={t0}>{t4}</AccordionSummary>;
    $[11] = t0;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      padding: "12px 0px 24px 36px"
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== props) {
    t7 = <AccordionDetails sx={t6}><ResourcesSelector {...props} onFilterChange={props.onFilterChange} /></AccordionDetails>;
    $[15] = props;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== isExpanded || $[18] !== t5 || $[19] !== t7) {
    t8 = <Accordion expanded={isExpanded}>{t5}{t7}</Accordion>;
    $[17] = isExpanded;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
export const ResourcesSelector = t0 => {
  const $ = _c(60);
  const {
    description,
    allResources,
    filteredResources,
    resources,
    addResource,
    removeResource,
    disabled,
    disabledDeletion,
    freeTextAdd: t1,
    initialResourcesIds,
    onFilterChange
  } = t0;
  const freeTextAdd = t1 === undefined ? true : t1;
  const [filter, setFilter] = useState("");
  const [highlighted, setHighlighted] = useState(null);
  const dropdownResources = filteredResources ?? allResources;
  let t2;
  if ($[0] !== addResource || $[1] !== onFilterChange || $[2] !== resources) {
    t2 = id => {
      if (!resources.includes(id) && id.length > 0) {
        addResource(id);
        setFilter("");
        onFilterChange?.("");
        setHighlighted(null);
      }
    };
    $[0] = addResource;
    $[1] = onFilterChange;
    $[2] = resources;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onAddPressed = t2;
  let t3;
  if ($[4] !== dropdownResources || $[5] !== resources) {
    let t4;
    if ($[7] !== resources) {
      t4 = resource => !resources.includes(resource.providerId);
      $[7] = resources;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = dropdownResources.filter(t4).filter(_temp);
    $[4] = dropdownResources;
    $[5] = resources;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const options = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      marginY: "12px 24px",
      gap: "24px"
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      gap: "12px",
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      flexGrow: 1,
      "& .MuiAutocomplete-endAdornment": {
        display: "none"
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      listbox: {
        component: VirtualizedListbox
      }
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = freeTextAdd ? "Resource not discovered yet. You can add it now to include it once available." : "No resources found.";
  let t9;
  if ($[13] !== onFilterChange) {
    t9 = (event, newInputValue) => {
      if (event) {
        setFilter(newInputValue);
        onFilterChange?.(newInputValue);
      }
    };
    $[13] = onFilterChange;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (event_0, option_1) => {
      setHighlighted(option_1);
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== onAddPressed) {
    t11 = (event_1, value, reason) => {
      if (reason === "selectOption" && value) {
        onAddPressed(value.providerId);
      }
    };
    $[16] = onAddPressed;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== filter || $[19] !== freeTextAdd || $[20] !== highlighted || $[21] !== onAddPressed) {
    t12 = params => <TextField {...params} sx={{
      "& .MuiInputBase-root": {
        height: "32px"
      },
      "& .MuiInputBase-input": {
        padding: 0,
        height: "auto"
      }
    }} placeholder="Search or type resource ID..." slotProps={{
      ...params.slotProps,
      input: {
        ...params.slotProps.input
      }
    }} onKeyDown={e => {
      if (e.key === "Enter") {
        if (highlighted) {
          e.preventDefault();
          e.stopPropagation();
          onAddPressed(highlighted.providerId);
          return;
        }
        if (freeTextAdd) {
          onAddPressed(filter);
        }
      }
    }} />;
    $[18] = filter;
    $[19] = freeTextAdd;
    $[20] = highlighted;
    $[21] = onAddPressed;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== disabled || $[24] !== filter || $[25] !== options || $[26] !== t11 || $[27] !== t12 || $[28] !== t8 || $[29] !== t9) {
    t13 = <Autocomplete disabled={disabled} sx={t6} clearOnBlur={false} options={options} value={null} filterOptions={_temp2} slotProps={t7} getOptionLabel={_temp3} renderOption={_temp4} noOptionsText={t8} inputValue={filter} onInputChange={t9} onHighlightChange={t10} onChange={t11} renderInput={t12} />;
    $[23] = disabled;
    $[24] = filter;
    $[25] = options;
    $[26] = t11;
    $[27] = t12;
    $[28] = t8;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== disabled || $[32] !== filter || $[33] !== freeTextAdd || $[34] !== onAddPressed) {
    t14 = freeTextAdd && <AddButton disabled={!filter || !!disabled} onClick={() => onAddPressed(filter)} />;
    $[31] = disabled;
    $[32] = filter;
    $[33] = freeTextAdd;
    $[34] = onAddPressed;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <Stack direction="row" sx={t5}>{t13}{t14}</Stack>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== description) {
    t16 = description && <Typography variant="subtitle1">{description}</Typography>;
    $[39] = description;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      gap: "12px"
    };
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== allResources || $[43] !== disabledDeletion || $[44] !== initialResourcesIds || $[45] !== removeResource || $[46] !== resources) {
    let t19;
    if ($[48] !== allResources) {
      t19 = r_0 => allResources.find(resource_1 => resource_1.providerId === r_0) || {
        id: "",
        providerId: r_0,
        name: "Not discovered yet"
      };
      $[48] = allResources;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    let t20;
    if ($[50] !== disabledDeletion || $[51] !== initialResourcesIds || $[52] !== removeResource) {
      t20 = resource_3 => <Stack key={resource_3.providerId} direction="row" sx={{
        gap: "12px",
        alignItems: "center",
        justifyContent: "space-between"
      }}><ResourceIdentifierItem resource={resource_3} /><OptionMenu iconClassName="text-(--mui-palette-text-primary)" options={[{
          text: "Remove",
          menuItemProps: {
            disabled: disabledDeletion && initialResourcesIds?.includes(resource_3.providerId),
            onClick: () => removeResource(resource_3.providerId)
          },
          id: "remove-resource"
        }]} /></Stack>;
      $[50] = disabledDeletion;
      $[51] = initialResourcesIds;
      $[52] = removeResource;
      $[53] = t20;
    } else {
      t20 = $[53];
    }
    t18 = resources.map(t19).filter(_temp5).map(t20);
    $[42] = allResources;
    $[43] = disabledDeletion;
    $[44] = initialResourcesIds;
    $[45] = removeResource;
    $[46] = resources;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[54] !== t18) {
    t19 = <Stack sx={t17}>{t18}</Stack>;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t15 || $[57] !== t16 || $[58] !== t19) {
    t20 = <Stack sx={t4}>{t15}{t16}{t19}</Stack>;
    $[56] = t15;
    $[57] = t16;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  return t20;
};
function _temp(resource_0, index, self) {
  return self.findIndex(r => r.providerId === resource_0.providerId) === index;
}
function _temp2(x) {
  return x;
}
function _temp3(option) {
  return typeof option === "string" ? option : option.providerId;
}
function _temp4(props, option_0) {
  return <li {...props} key={option_0.providerId}><ResourceIdentifierItem resource={option_0} /></li>;
}
function _temp5(resource_2) {
  return resource_2 !== undefined;
}